import { useState, useEffect, useCallback } from 'react';
import { PCCase } from '../types';
import { PCCaseQueryParams } from '../services/caseService';
import {
  fetchCases,
  fetchCaseById,
  fetchCasesByIds,
  fetchManufacturers,
  fetchCaseStats,
  StatsQueryParams,
} from '../services/caseService';

interface UseCasesResult {
  cases: PCCase[];
  loading: boolean;
  error: Error | null;
  totalCount: number;
  page: number;
  totalPages: number;
  refetch: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export const useCases = (params: PCCaseQueryParams = {}): UseCasesResult => {
  const [cases, setCases] = useState<PCCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(params.page || 1);
  const [totalPages, setTotalPages] = useState(1);

  // Create a stable key from params for dependency tracking
  const paramsKey = JSON.stringify({
    search: params.search,
    sizeCategories: params.sizeCategories,
    formFactors: params.formFactors,
    manufacturers: params.manufacturers,
    sidePanelTypes: params.sidePanelTypes,
    minGpuLength: params.minGpuLength,
    minCoolerHeight: params.minCoolerHeight,
    minPsuLength: params.minPsuLength,
    minRadiatorSize: params.minRadiatorSize,
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    pageSize: params.pageSize,
  });

  const fetchData = useCallback(async (currentPage: number, append = false) => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchCases({ ...params, page: currentPage });

      setCases(prev => append ? [...prev, ...result.data] : result.data);
      setTotalCount(result.count);
      setPage(result.page);
      setTotalPages(result.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch cases'));
    } finally {
      setLoading(false);
    }
  }, [paramsKey]);

  useEffect(() => {
    fetchData(1, false);
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData(1, false);
  }, [fetchData]);

  const loadMore = useCallback(() => {
    if (page < totalPages && !loading) {
      fetchData(page + 1, true);
    }
  }, [fetchData, page, totalPages, loading]);

  return {
    cases,
    loading,
    error,
    totalCount,
    page,
    totalPages,
    refetch,
    loadMore,
    hasMore: page < totalPages,
  };
};

// Hook for fetching a single case
export const useCase = (id: string | null) => {
  const [pcCase, setPcCase] = useState<PCCase | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) {
      setPcCase(null);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await fetchCaseById(id);
        setPcCase(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch case'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  return { pcCase, loading, error };
};

// Hook for fetching multiple cases by IDs
export const useCasesByIds = (ids: string[]) => {
  const [cases, setCases] = useState<PCCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const idsKey = ids.join(',');

  useEffect(() => {
    if (ids.length === 0) {
      setCases([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await fetchCasesByIds(ids);
        setCases(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch cases'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [idsKey]);

  return { cases, loading, error };
};

// Hook for fetching manufacturers
export const useManufacturers = () => {
  const [manufacturers, setManufacturers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await fetchManufacturers();
        setManufacturers(result);
      } catch (err) {
        console.error('Failed to fetch manufacturers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return { manufacturers, loading };
};

// Hook for fetching case stats
export const useCaseStats = (params?: StatsQueryParams) => {
  const [stats, setStats] = useState<{ total: number; byCategory: Record<string, number> }>({
    total: 0,
    byCategory: {},
  });
  const [loading, setLoading] = useState(true);

  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await fetchCaseStats(params);
        setStats(result);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { stats, loading };
};
